import Button from "@mui/material/Button";
import { useStyle } from "@/components/button/style";
import { t } from "i18next";
import { useTheme } from "@/contexts/theme";
import { IIconProps } from "@/components/icons/interface";

/** Lucide-style download icon for non-HP themes */
const DefaultDownloadIcon = ({ width, height, color }: IIconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width ?? 24}
    height={height ?? 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color ?? "currentColor"}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 15V3" />
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="m7 10 5 5 5-5" />
  </svg>
);

type DownloadExcelButtonVariant = "outlined" | "contained";

interface IDownloadExcelButtonProps {
  label: string;
  onClick: () => void;
  variant?: DownloadExcelButtonVariant;
  style?: React.CSSProperties;
  className?: string;
}

const DownloadExcelButton = ({
  label,
  onClick,
  variant = "outlined",
  style,
  className,
}: IDownloadExcelButtonProps) => {
  const { classes, primaryColor } = useStyle();
  const { currentTheme, getIconOverride } = useTheme();
  const dir: "rtl" | "ltr" = t("direction");
  const RTL = dir === "rtl";
  const isHpClient = currentTheme === "hp-client";
  const DownloadIconOverride = getIconOverride("DownloadIcon");
  const isContained = variant === "contained";

  const hpBase = {
    boxSizing: "border-box" as const,
    display: "flex",
    flexDirection: "row" as const,
    justifyContent: "center",
    alignItems: "center",
    padding: RTL ? "12px 16px 12px 20px" : "12px 20px 12px 16px",
    gap: "8px",
    height: "var(--theme-button-height, 44px)",
    borderRadius: "var(--theme-button-radius, 8px)",
    flex: "none",
    flexGrow: 0,
    textTransform: "none" as const,
    fontFamily:
      "var(--theme-typography-button-font-family, 'Forma DJR UI', sans-serif)",
    fontWeight: "var(--theme-font-weight-light, 300) !important",
    fontSize: "16px",
    lineHeight: "var(--theme-typography-label-regular-line-height, 20px)",
    boxShadow: "none",
    "& .MuiButton-startIcon": {
      margin: 0,
      ...(RTL ? { marginLeft: "8px" } : { marginRight: "8px" }),
    },
  };

  const hpOutlined = {
    ...hpBase,
    border: "1px solid var(--theme-primary-color, #024AD8)",
    backgroundColor: "#FFFFFF",
    color: "var(--theme-primary-color, #024AD8)",
    "&:hover": {
      backgroundColor: "var(--theme-button-bg-hover-outlined)",
    },
  };

  const hpContained = {
    ...hpBase,
    border: "none",
    backgroundColor: "var(--theme-primary-color, #024AD8)",
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: "var(--theme-primary-color-hover, #0036C4)",
    },
  };

  const nonHpOutlined = {
    ...classes.addButtonStyle,
    "& .MuiButton-startIcon": {
      ...(RTL
        ? { marginLeft: "4px", marginRight: 0 }
        : { marginRight: "4px", marginLeft: 0 }),
    },
  };

  const nonHpContained = {
    ...classes.addButtonStyle,
    border: "none",
    background: primaryColor(500),
    color: "#FFF",
    "&:hover": {
      backgroundColor: primaryColor(600),
    },
    "& .MuiButton-startIcon": {
      ...(RTL
        ? { marginLeft: "4px", marginRight: 0 }
        : { marginRight: "4px", marginLeft: 0 }),
    },
  };

  const buttonSx = isHpClient
    ? isContained
      ? hpContained
      : hpOutlined
    : isContained
      ? nonHpContained
      : nonHpOutlined;

  const iconColor = isHpClient
    ? isContained
      ? "#FFFFFF"
      : "var(--theme-primary-color, #024AD8)"
    : isContained
      ? "#FFFFFF"
      : primaryColor(500);

  return (
    <Button
      variant="outlined"
      sx={{ ...buttonSx, ...style }}
      startIcon={
        DownloadIconOverride ? (
          <DownloadIconOverride color={iconColor} />
        ) : (
          <DefaultDownloadIcon color={iconColor} />
        )
      }
      onClick={onClick}
      className={className}
    >
      {label}
    </Button>
  );
};

export { DownloadExcelButton };
